/**
 * 68.22 step 1 (DIRECTION §0.9, the play's base of fire): where a support squad stands to cover an objective — the owner:
 * *"squad 1 cover from 50% effective range where cover is present"*. Pure; read when a play is issued and re-checked
 * every few seconds (step 2), never stored.
 *
 * **The band.** The rifle's effective range is where its spread cone grows as wide as a person (`rifleEffectivePeople`:
 * a body's half-width over the spread's half-angle — 25 people with today's rifle); the support stands at `share` of it,
 * ± `tolerance` (half: about 12 people out, the range most kills in the sweeps happen at).
 *
 * **The choice**, nearest the support squad (`from`) in each kind, the first kind found winning:
 * 1. **cover** — one of lane F's cover points (core/coverMap.ts `coverPointsNear`, the cover system's shared contract) in
 *    the band, that hides at least a crouching body from the objective (its shelter on the objective's bearing) and has a
 *    peek whose standing eye sees the objective (the person's line and the props, as sight does);
 * 2. **crest** — a crest of the relief grid (`terrainSpotsNear`) in the band that sees the objective;
 * 3. **open** — the band's middle on the line from the objective toward the squad.
 */
import type { CoverPoint, GameState, Vec3 } from '../types';
import { GROUND_WEAPONS } from '../constants';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub, vecLength } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { coverBearing, coverPointsNear, terrainSpotsNear } from './coverMap';
import { eyePointAt } from './los';
import { personLineOfSight } from './fineTerrain';
import { segmentCovered } from './bullets';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';

/** The dials: the share of the rifle's effective range the support stands at, its tolerance, and a body's half-width (people). */
export const SUPPORT_POSITION = { share: 0.5, tolerance: 0.3, bodyHalfWidthPeople: 0.25 };

/** The rifle's effective range in people: where its spread cone is as wide as a person. */
export function rifleEffectivePeople(): number {
  return SUPPORT_POSITION.bodyHalfWidthPeople / GROUND_WEAPONS.rifle.spread;
}

export interface SupportPosition {
  planetIndex: number;
  /** Where the support stands (a unit surface direction). */
  dir: Vec3;
  kind: 'cover' | 'crest' | 'open';
  /** The cover point, when the kind is cover. */
  cover?: CoverPoint;
  /** How far from the objective, in people along the ground. */
  fromObjectivePeople: number;
}

/**
 * What the choice reads of the objective alone, whoever asks: the band's covers and (none there) its crests. A play reads it
 * once for all its squads (core/plays.ts): only the nearest to each squad differs.
 */
export interface SupportSites {
  planetIndex: number;
  obj: Vec3;
  person: number;
  radius: number;
  covers: CoverPoint[];
  crests: Vec3[];
}

/** The objective's support sites on planet `planetIndex`; null without a planet. */
export function supportSites(state: GameState, planetIndex: number, objective: Vec3): SupportSites | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state);
  const person = PERSON_BODY_HEIGHT * ps.k;
  const obj = vecNormalize(objective);
  const arcPeople = (a: Vec3, b: Vec3) => (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * planet.radius) / person;
  const mid = SUPPORT_POSITION.share * rifleEffectivePeople();
  const lo = mid * (1 - SUPPORT_POSITION.tolerance), hi = mid * (1 + SUPPORT_POSITION.tolerance);
  const inBand = (d: Vec3) => { const x = arcPeople(d, obj); return x >= lo && x <= hi; };
  const objEye = eyePointAt(planet, obj, ps.losEyeHeight);
  const sees = (d: Vec3) => {
    const eye = eyePointAt(planet, d, ps.losEyeHeight);
    return personLineOfSight(eye, objEye, state) && !segmentCovered(eye, objEye, state, undefined, ps.coverSampleStep);
  };
  // 1. cover in the band, hiding a crouching body from the objective, with a peek that sees it
  const covers = coverPointsNear(state, planetIndex, obj, hi * person).filter(c => {
    if (c.concealment || !inBand(c.dir)) return false;
    const hides = c.shelter[coverBearing(c, objEye)];
    if (hides !== 'stand' && hides !== 'crouch') return false;
    return c.peeks.some(p => sees(p.dir));
  });
  // 2. a crest in the band that sees the objective (read only where no cover is)
  const crests = covers.length ? [] : terrainSpotsNear(state, planetIndex, obj, hi * person, objEye).filter(t => t.kind === 'crest' && inBand(t.dir) && sees(t.dir)).map(t => t.dir);
  return { planetIndex, obj, person, radius: planet.radius, covers, crests };
}

/** The support position for a squad at `from` among the objective's sites: the nearest cover, else the nearest crest, else open. */
export function supportPositionAmong(sites: SupportSites, from: Vec3): SupportPosition {
  const { planetIndex, obj, person } = sites, at = vecNormalize(from);
  const arcPeople = (a: Vec3, b: Vec3) => (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * sites.radius) / person;
  const mid = SUPPORT_POSITION.share * rifleEffectivePeople();
  const nearest = <T>(xs: T[], dirOf: (x: T) => Vec3): T | null =>
    xs.reduce<T | null>((best, x) => (best === null || arcPeople(dirOf(x), at) < arcPeople(dirOf(best), at) ? x : best), null);
  const place = (dir: Vec3, kind: SupportPosition['kind'], cover?: CoverPoint): SupportPosition =>
    ({ planetIndex, dir, kind, ...(cover ? { cover } : {}), fromObjectivePeople: arcPeople(dir, obj) });
  const cover = nearest(sites.covers, c => c.dir);
  if (cover) return place(cover.dir, 'cover', cover);
  const crest = nearest(sites.crests, d => d);
  if (crest) return place(crest, 'crest');
  // 3. open ground: the band's middle, on the line from the objective toward the squad
  const t = vecSub(at, vecScale(obj, vecDot(at, obj)));
  const way = vecLength(t) > 1e-9 ? vecNormalize(t) : vecNormalize(vecSub({ x: 0, y: 1, z: 0 }, vecScale(obj, obj.y)));
  const a = (mid * person) / sites.radius;
  return place(vecNormalize(vecAdd(vecScale(obj, dcos(a)), vecScale(way, dsin(a)))), 'open');
}

/** The support position for a squad at `from` covering the objective at `objective` (planet `planetIndex`); null without a planet. */
export function supportPosition(state: GameState, planetIndex: number, objective: Vec3, from: Vec3): SupportPosition | null {
  const sites = supportSites(state, planetIndex, objective);
  return sites && supportPositionAmong(sites, from);
}
